import { useEffect, useState } from "react";
import i18next from "src/i18n/config";
import { LocalStorage } from "src/services/localStorageService";

import { getTenantDomainOverride, toAppUrl } from "@services/configService";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";

type Option = { label: string; value: string };

const defaultOption: Option = {
    label: i18next.t("pages:settingsPage.portalSettings.tenantSwitch.defaultOption"),
    value: "default",
};

export const TenantSwitcher = () => {
    // List is loaded at mount from /dev-tenants/_index.json. In dev a Vite middleware serves it
    // (see vite.config.mts); on staging deploys the build plugin writes it into dist/dev-tenants/.
    // Production deploys don't upload the folder, so the fetch 404s and the dropdown stays at
    // just "Default" — by design: tenant configs must not ship to production buckets.
    const [options, setOptions] = useState<Option[]>([defaultOption]);

    useEffect(() => {
        const ctrl = new AbortController();
        fetch(toAppUrl("/dev-tenants/_index.json"), { cache: "no-store", signal: ctrl.signal })
            .then((res) => (res.ok ? (res.json() as Promise<Option[]>) : []))
            .then((tenants) => {
                setOptions([defaultOption, ...tenants.sort((a, b) => a.label.localeCompare(b.label))]);
            })
            .catch(() => {
                // Dev-only endpoint; in deployed envs there's nothing to do.
            });
        return () => ctrl.abort();
    }, []);

    const onChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value !== "string") {
            return;
        }

        LocalStorage.setItem("domainOverride", selectedOption.value);
        window.location.reload();
    };

    const value = getTenantDomainOverride();
    return (
        <SelectField
            disableSorting
            value={options.find((option) => option.value === value)}
            options={options}
            onChange={onChange}
        />
    );
};
